import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Chip,
  Card,
  CardMedia,
  CardContent,
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Switch,
  Tooltip,
  Divider,
  ToggleButtonGroup,
  ToggleButton,
  Avatar,
  TextField,
  FormControl,
  Select,
  MenuItem,
  Link,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import LockIcon from "@mui/icons-material/Lock";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ThumbDownIcon from "@mui/icons-material/ThumbDown";
import PeopleIcon from "@mui/icons-material/People";
import GridViewIcon from "@mui/icons-material/GridView";
import ViewListIcon from "@mui/icons-material/ViewList";
import PublishIcon from "@mui/icons-material/Publish";
import DeleteIcon from "@mui/icons-material/Delete";
import FavoriteIcon from "@mui/icons-material/Favorite";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import EditIcon from "@mui/icons-material/Edit";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SettingsIcon from "@mui/icons-material/Settings";
import VerifiedIcon from "@mui/icons-material/Verified";
import ShareIcon from "@mui/icons-material/Share";
import { useParams, Link as RouterLink } from "react-router-dom";
import { Helmet } from "react-helmet";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { BASE_API_URL, BASE_SITE_URL } from "../config";
import { getToken, authFetch } from "../Scripts/authHelper";
import { SUPPORTED_LANGUAGES } from "../languages";
import { useThemeMode } from "../Styles/ThemeModeContext";

const ToggleRow = ({ label, caption, checked, onChange, disabled }: {
  label: string;
  caption: string;
  checked: boolean;
  onChange: () => void;
  disabled?: boolean;
}) => (
  <Box sx={{
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    py: 1.5,
  }}>
    <Box sx={{ pr: 2 }}>
      <Typography variant="body2" sx={{ fontWeight: 500 }}>{label}</Typography>
      <Typography variant="caption" color="text.secondary">{caption}</Typography>
    </Box>
    <Switch checked={checked} onChange={onChange} disabled={disabled} size="small" />
  </Box>
);

const Profile = (props?: any) => {
  const { t, i18n } = useTranslation();
  const { userId } = useParams<{ userId: string }>();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Profile data
  const [profile, setProfile] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Capture viewing
  const [viewingCapture, setViewingCapture] = useState<any>(null);
  const [viewLoading, setViewLoading] = useState(false);
  const [togglingPublish, setTogglingPublish] = useState(false);
  const [togglingTry, setTogglingTry] = useState(false);
  const [unpublishingCard, setUnpublishingCard] = useState<number | null>(null);
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");

  // Delete
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);

  // Edit title/description
  const [editingTitle, setEditingTitle] = useState(false);
  const [editingDescription, setEditingDescription] = useState(false);
  const [editTitle, setEditTitle] = useState("");
  const [editDescription, setEditDescription] = useState("");

  // Liked section
  const [likedItems, setLikedItems] = useState<any[]>([]);
  const [likedLoading, setLikedLoading] = useState(false);
  const [likedLoaded, setLikedLoaded] = useState(false);
  const [activeSection, setActiveSection] = useState<"tierlists" | "grids" | "liked">("tierlists");

  // Inline bio editing
  const [editingBio, setEditingBio] = useState(false);
  const [bioValue, setBioValue] = useState("");
  const [bioSaving, setBioSaving] = useState(false);

  // Profile picture
  const [uploadingPic, setUploadingPic] = useState(false);

  // Verification
  const [verifyReason, setVerifyReason] = useState("");
  const [verifySubmitting, setVerifySubmitting] = useState(false);
  const [verifyStatus, setVerifyStatus] = useState({ type: "", message: "" });

  // Settings state (owner only)
  const [language, setLanguage] = useState(i18n.language || "en");
  const { preference: themePreference, setPreference: setThemePreference } = useThemeMode();
  const [privacyMode, setPrivacyMode] = useState(
    localStorage.getItem("privacyMode") === "true"
  );
  const [promoEmails, setPromoEmails] = useState(
    props?.state?.userProfileInfo?.promotional_emails !== 0
  );
  const [promoSaving, setPromoSaving] = useState(false);
  const [publicProfile, setPublicProfile] = useState(
    props?.state?.userProfileInfo?.is_profile_public !== 0
  );
  const [profileSaving, setProfileSaving] = useState(false);

  // Username setup
  const [aliasName, setAliasName] = useState("");
  const [aliasStatus, setAliasStatus] = useState({ type: "", message: "" });
  const [isSavingAlias, setIsSavingAlias] = useState(false);
  const [usernameAvailability, setUsernameAvailability] = useState<{
    checking: boolean;
    available: boolean | null;
    message: string;
  }>({ checking: false, available: null, message: "" });

  const currentUserId = props?.state?.user?.id;
  const isOwner = currentUserId && profile?.id === currentUserId;
  const postCommands = props?.postCommands;
  const updateCommands = props?.updateCommands;

  const existingAlias = props?.state?.user?.aliasName || props?.state?.userProfileInfo?.alias_name;
  const hasExistingUsername = Boolean(existingAlias);

  // Username availability check with debounce
  useEffect(() => {
    if (hasExistingUsername || !aliasName || aliasName.trim().length < 2) {
      setUsernameAvailability({ checking: false, available: null, message: "" });
      return;
    }

    const timer = setTimeout(async () => {
      setUsernameAvailability({ checking: true, available: null, message: "" });
      try {
        const res = await fetch(
          `${BASE_API_URL}/api/check-username/${encodeURIComponent(aliasName.trim())}`
        );
        const data = await res.json();
        setUsernameAvailability({
          checking: false,
          available: data.available,
          message: data.available ? "Username is available!" : data.reason,
        });
      } catch {
        setUsernameAvailability({
          checking: false,
          available: null,
          message: "Could not check availability",
        });
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [aliasName, hasExistingUsername]);

  // ── Handlers ──

  const handleCardClick = (capture: any) => {
    setViewingCapture(capture);
    setViewLoading(true);
    fetch(`${BASE_API_URL}/api/gallery/${capture.Id}`)
      .then((res) => res.ok ? res.json() : null)
      .then((data) => {
        if (data) setViewingCapture(data);
      })
      .catch(() => {})
      .finally(() => setViewLoading(false));
  };

  const handleTogglePublishCard = async (e: React.MouseEvent, capture: any) => {
    e.stopPropagation();
    if (!postCommands || unpublishingCard) return;
    setUnpublishingCard(capture.Id);
    const wasPublic = capture.is_public === 1;
    try {
      if (wasPublic) {
        await postCommands.unpublishFromGallery(capture.Id);
      } else {
        await postCommands.publishToGallery(capture.Id, capture.thumbnail);
      }
      setProfile((prev: any) => ({
        ...prev,
        captures: prev.captures.map((c: any) =>
          c.Id === capture.Id ? { ...c, is_public: wasPublic ? 0 : 1 } : c
        ),
        total_published: wasPublic
          ? Math.max((prev.total_published || 1) - 1, 0)
          : (prev.total_published || 0) + 1,
      }));
    } catch (err) {
      console.error("Error toggling publish:", err);
    } finally {
      setUnpublishingCard(null);
    }
  };

  const handleSaveTitle = async () => {
    if (!viewingCapture || !postCommands) return;
    const trimmed = editTitle.trim().slice(0, 100);
    if (trimmed === (viewingCapture.title || "")) {
      setEditingTitle(false);
      return;
    }
    const result = await postCommands.updateCapture(viewingCapture.Id, trimmed);
    if (result?.success) {
      setViewingCapture({ ...viewingCapture, title: trimmed });
      if (profile?.captures) {
        const updated = profile.captures.map((c: any) =>
          c.Id === viewingCapture.Id ? { ...c, title: trimmed } : c
        );
        setProfile({ ...profile, captures: updated });
      }
      toast.success(t("toast.titleUpdated"));
    }
    setEditingTitle(false);
  };

  const handleSaveDescription = async () => {
    if (!viewingCapture || !postCommands) return;
    const trimmed = editDescription.trim().slice(0, 500);
    if (trimmed === (viewingCapture.description || "")) {
      setEditingDescription(false);
      return;
    }
    const result = await postCommands.updateCapture(viewingCapture.Id, undefined, trimmed);
    if (result?.success) {
      setViewingCapture({ ...viewingCapture, description: trimmed });
      if (profile?.captures) {
        const updated = profile.captures.map((c: any) =>
          c.Id === viewingCapture.Id ? { ...c, description: trimmed } : c
        );
        setProfile({ ...profile, captures: updated });
      }
      toast.success(t("toast.descriptionUpdated"));
    }
    setEditingDescription(false);
  };

  const handleTogglePublish = async () => {
    if (!viewingCapture || !postCommands) return;
    setTogglingPublish(true);
    const wasPublic = viewingCapture.is_public === 1;
    try {
      if (wasPublic) {
        await postCommands.unpublishFromGallery(viewingCapture.Id);
      } else {
        await postCommands.publishToGallery(viewingCapture.Id, viewingCapture.thumbnail);
      }
      setViewingCapture((prev: any) => ({ ...prev, is_public: wasPublic ? 0 : 1 }));
      setProfile((prev: any) => ({
        ...prev,
        captures: prev.captures.map((c: any) =>
          c.Id === viewingCapture.Id ? { ...c, is_public: wasPublic ? 0 : 1 } : c
        ),
        total_published: wasPublic
          ? Math.max((prev.total_published || 1) - 1, 0)
          : (prev.total_published || 0) + 1,
      }));
    } catch (err) {
      console.error("Error toggling publish:", err);
    } finally {
      setTogglingPublish(false);
    }
  };

  const handleToggleTry = async () => {
    if (!viewingCapture || !postCommands) return;
    setTogglingTry(true);
    try {
      const result = await postCommands.toggleAllowTry(viewingCapture.Id);
      if (result) {
        setViewingCapture((prev: any) => ({ ...prev, allow_try: result.allow_try }));
        setProfile((prev: any) => ({
          ...prev,
          captures: prev.captures.map((c: any) =>
            c.Id === viewingCapture.Id ? { ...c, allow_try: result.allow_try } : c
          ),
        }));
      }
    } catch (err) {
      console.error("Error toggling public use:", err);
    } finally {
      setTogglingTry(false);
    }
  };

  const handleDeleteCapture = async (captureId: number) => {
    if (!postCommands) return;
    setDeletingId(captureId);
    try {
      const result = await postCommands.deleteCapture(captureId);
      if (result?.success) {
        setProfile((prev: any) => ({
          ...prev,
          captures: prev.captures.filter((c: any) => c.Id !== captureId),
          total_published: prev.captures.find((c: any) => c.Id === captureId)?.is_public === 1
            ? Math.max((prev.total_published || 1) - 1, 0)
            : prev.total_published,
        }));
        if (viewingCapture?.Id === captureId) setViewingCapture(null);
      }
    } catch (err) {
      console.error("Error deleting capture:", err);
    } finally {
      setDeletingId(null);
      setConfirmDeleteId(null);
    }
  };

  const fetchLikedItems = async () => {
    if (likedLoaded || likedLoading) return;
    setLikedLoading(true);
    try {
      const res = await authFetch(`${BASE_API_URL}/api/gallery/my-liked`);
      const data = await res.json();
      setLikedItems(data.items || []);
      setLikedLoaded(true);
    } catch {
    } finally {
      setLikedLoading(false);
    }
  };

  // Bio
  const handleStartEditBio = () => {
    setBioValue(profile?.bio || "");
    setEditingBio(true);
  };

  const handleSaveBio = async () => {
    if (!updateCommands) return;
    setBioSaving(true);
    try {
      const response = await updateCommands.updateBio(bioValue);
      if (response.ok) {
        setProfile((prev: any) => ({ ...prev, bio: bioValue }));
        setEditingBio(false);
      }
    } catch {
    } finally {
      setBioSaving(false);
    }
  };

  // Verification request
  const handleRequestVerification = async () => {
    if (!postCommands || verifyReason.trim().length < 10) return;
    setVerifySubmitting(true);
    setVerifyStatus({ type: "", message: "" });
    try {
      const result = await postCommands.requestVerification(verifyReason.trim());
      if (result.success) {
        setVerifyStatus({ type: "success", message: "Request submitted!" });
        setProfile((prev: any) => ({ ...prev, has_pending_verification: true }));
        setVerifyReason("");
      } else {
        setVerifyStatus({ type: "error", message: result.error || "Failed to submit" });
      }
    } catch {
      setVerifyStatus({ type: "error", message: "Failed to submit request" });
    } finally {
      setVerifySubmitting(false);
    }
  };

  // Profile picture
  const resizeImage = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement("canvas");
          const size = 200;
          canvas.width = size;
          canvas.height = size;
          const ctx = canvas.getContext("2d");
          if (!ctx) { reject("No canvas context"); return; }
          // Square crop from center
          const minDim = Math.min(img.width, img.height);
          const sx = (img.width - minDim) / 2;
          const sy = (img.height - minDim) / 2;
          ctx.drawImage(img, sx, sy, minDim, minDim, 0, 0, size, size);
          resolve(canvas.toDataURL("image/jpeg", 0.8));
        };
        img.onerror = () => reject("Failed to load image");
        img.src = reader.result as string;
      };
      reader.onerror = () => reject("Failed to read file");
      reader.readAsDataURL(file);
    });
  };

  const handleProfilePicUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !updateCommands) return;
    setUploadingPic(true);
    try {
      const resized = await resizeImage(file);
      const response = await updateCommands.updateProfilePicture(resized);
      if (response.ok) {
        setProfile((prev: any) => ({ ...prev, profile_picture: resized }));
        // Update global state so header avatar updates
        if (props?.basicFunctions?.updateState) {
          props.basicFunctions.updateState({
            userProfileInfo: { ...props.state.userProfileInfo, profile_picture: resized },
          });
        }
      }
    } catch (err) {
      console.error("Error uploading profile picture:", err);
    } finally {
      setUploadingPic(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  // Settings handlers
  const handleLanguageChange = (event: any) => {
    const newLanguage = event.target.value;
    setLanguage(newLanguage);
    localStorage.setItem("language", newLanguage);
    props?.basicFunctions?.updateState({ language: newLanguage });
    i18n.changeLanguage(newLanguage);
  };

  const handlePrivacyModeToggle = () => {
    const newValue = !privacyMode;
    setPrivacyMode(newValue);
    localStorage.setItem("privacyMode", String(newValue));
    props?.basicFunctions?.updateState({ privacyMode: newValue });
  };

  const handleSaveAlias = async () => {
    setIsSavingAlias(true);
    setAliasStatus({ type: "", message: "" });
    try {
      const response = await updateCommands.updateAliasName(aliasName);
      if (response.ok) {
        setAliasStatus({ type: "success", message: "Username saved! This is permanent." });
        props?.basicFunctions?.updateState({
          user: { ...props.state.user, aliasName: aliasName.trim() },
        });
      } else {
        const data = await response.json();
        setAliasStatus({ type: "error", message: data.error || "Failed to save username." });
      }
    } catch {
      setAliasStatus({ type: "error", message: "Failed to save username." });
    } finally {
      setIsSavingAlias(false);
    }
  };

  // Fetch profile
  useEffect(() => {
    if (!userId) return;
    setLoading(true);
    setError(null);
    const headers: Record<string, string> = {};
    const token = getToken();
    if (token) headers["Authorization"] = `Bearer ${token}`;

    fetch(`${BASE_API_URL}/api/profile/${userId}`, { headers })
      .then((res) => {
        if (res.status === 403) {
          setError("private");
          setLoading(false);
          return null;
        }
        if (!res.ok) {
          setError("not_found");
          setLoading(false);
          return null;
        }
        return res.json();
      })
      .then((data) => {
        if (data) {
          setProfile(data);
          setLoading(false);
        }
      })
      .catch(() => {
        setError("not_found");
        setLoading(false);
      });
  }, [userId]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error === "private") {
    return (
      <Box sx={{ maxWidth: 800, mx: "auto", px: 3, py: 8, textAlign: "center" }}>
        <Helmet>
          <title>Private Profile | Music Tier List</title>
        </Helmet>
        <LockIcon sx={{ fontSize: 48, opacity: 0.4, mb: 2 }} />
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
          This profile is private
        </Typography>
        <Typography variant="body2" color="text.secondary">
          This user has chosen to keep their profile private.
        </Typography>
      </Box>
    );
  }

  if (error || !profile) {
    return (
      <Box sx={{ maxWidth: 800, mx: "auto", px: 3, py: 8, textAlign: "center" }}>
        <Helmet>
          <title>Profile Not Found | Music Tier List</title>
        </Helmet>
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
          Profile not found
        </Typography>
        <Typography variant="body2" color="text.secondary">
          This user doesn't exist or their profile is unavailable.
        </Typography>
      </Box>
    );
  }

  const displayName = profile.alias_name || profile.name || "Anonymous";
  const memberSince = profile.created_date
    ? new Date(profile.created_date).toLocaleDateString(undefined, {
        year: "numeric",
        month: "long",
      })
    : null;

  const getTypeLabel = (capture: any) => {
    if (!capture.list_type) return null;
    if (capture.group_by && capture.group_by !== "All")
      return `${capture.list_type} · ${capture.group_by}`;
    return capture.list_type;
  };

  const filteredCaptures = (profile?.captures || []).filter((c: any) =>
    activeSection === "grids" ? c.type?.startsWith("Grid") : !c.type?.startsWith("Grid")
  );

  return (
    <>
      <Helmet>
        <title>{displayName}'s Profile | Music Tier List</title>
        <meta
          name="description"
          content={`View ${displayName}'s music tier lists and rankings on Music Tier List.`}
        />
      </Helmet>
      <Box id="profile-page" sx={{
        px: 3,
        py: 4,
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        gap: 4,
      }}>
        {/* ── Left Sidebar ── */}
        <Box sx={{ width: { xs: "100%", md: 300 }, flexShrink: 0 }}>
          {/* Avatar */}
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", mb: 2 }}>
            <Box
              sx={{
                position: "relative",
                cursor: isOwner ? "pointer" : "default",
                "&:hover .camera-overlay": isOwner ? { opacity: 1 } : {},
              }}
              onClick={() => isOwner && fileInputRef.current?.click()}
            >
              <Avatar
                src={profile.profile_picture || undefined}
                sx={{
                  width: 100,
                  height: 100,
                  bgcolor: "var(--accent)",
                  fontSize: "2.5rem",
                  fontWeight: 700,
                }}
              >
                {(profile.name || "U")[0].toUpperCase()}
              </Avatar>
              {isOwner && (
                <Box
                  className="camera-overlay"
                  sx={{
                    position: "absolute",
                    inset: 0,
                    borderRadius: "50%",
                    bgcolor: "var(--on-media-bg)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    opacity: 0,
                    transition: "opacity 0.2s ease",
                  }}
                >
                  {uploadingPic ? (
                    <CircularProgress size={24} sx={{ color: "var(--on-media)" }} />
                  ) : (
                    <CameraAltIcon sx={{ color: "var(--on-media)" }} />
                  )}
                </Box>
              )}
            </Box>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              hidden
              onChange={handleProfilePicUpload}
            />
          </Box>

          {/* Display Name */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              {displayName}
            </Typography>
            {profile.is_verified && (
              <Tooltip title="Verified">
                <VerifiedIcon sx={{ fontSize: 22, color: "#1d9bf0" }} />
              </Tooltip>
            )}
          </Box>

          {/* Username */}
          {isOwner ? (
            hasExistingUsername ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.25 }}>
                <Typography variant="body2" color="text.secondary">@{existingAlias}</Typography>
                <CheckCircleOutlineIcon sx={{ fontSize: 14, color: "success.main" }} />
              </Box>
            ) : (
              <Box sx={{ mt: 1.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>Set Username</Typography>
                <TextField
                  fullWidth
                  size="small"
                  value={aliasName}
                  onChange={(e) => setAliasName(e.target.value.replace(/[^a-zA-Z0-9_]/g, ""))}
                  disabled={isSavingAlias}
                  placeholder="your_username"
                  inputProps={{ maxLength: 30 }}
                  helperText={
                    usernameAvailability.checking
                      ? "Checking..."
                      : usernameAvailability.message || "Letters, numbers, underscores. Permanent — email support@musictierlist.com to change later."
                  }
                  error={usernameAvailability.available === false}
                  color={usernameAvailability.available === true ? "success" : undefined}
                  sx={{ mb: 0.5 }}
                />
                <Button
                  variant="contained"
                  size="small"
                  fullWidth
                  onClick={handleSaveAlias}
                  disabled={
                    isSavingAlias ||
                    usernameAvailability.available === false ||
                    usernameAvailability.checking ||
                    !aliasName.trim() ||
                    aliasName.trim().length < 2
                  }
                  sx={{ textTransform: "none" }}
                >
                  Set Username
                </Button>
                {aliasStatus.message && (
                  <Alert
                    severity={aliasStatus.type === "success" ? "success" : "error"}
                    sx={{ mt: 1, py: 0 }}
                    variant="outlined"
                  >
                    {aliasStatus.message}
                  </Alert>
                )}
              </Box>
            )
          ) : profile.alias_name ? (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
              @{profile.alias_name}
            </Typography>
          ) : null}

          {/* Member since + published count */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mt: 1.5, flexWrap: "wrap" }}>
            {memberSince && (
              <Typography variant="caption" color="text.secondary">
                Member since {memberSince}
              </Typography>
            )}
            <Chip
              label={`${profile.total_published || 0} published`}
              size="small"
              sx={{
                height: 22,
                fontSize: "0.7rem",
                backgroundColor: "var(--hover-overlay)",
              }}
            />
          </Box>

          {/* Bio */}
          <Box sx={{ mt: 2 }}>
            {editingBio ? (
              <Box>
                <TextField
                  fullWidth
                  size="small"
                  value={bioValue}
                  onChange={(e) => setBioValue(e.target.value)}
                  disabled={bioSaving}
                  placeholder="Tell others about your music taste..."
                  inputProps={{ maxLength: 300 }}
                  multiline
                  rows={3}
                  autoFocus
                  sx={{ mb: 0.5 }}
                />
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Typography variant="caption" color="text.secondary" sx={{ opacity: 0.5 }}>
                    {bioValue.length}/300
                  </Typography>
                  <Box sx={{ display: "flex", gap: 0.5 }}>
                    <Button
                      size="small"
                      onClick={() => setEditingBio(false)}
                      disabled={bioSaving}
                      sx={{ textTransform: "none", minWidth: 0 }}
                    >
                      Cancel
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      onClick={handleSaveBio}
                      disabled={bioSaving}
                      sx={{ textTransform: "none", minWidth: 0 }}
                    >
                      {bioSaving ? <CircularProgress size={14} /> : "Save"}
                    </Button>
                  </Box>
                </Box>
              </Box>
            ) : (
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5 }}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    whiteSpace: "pre-wrap",
                    flex: 1,
                    minHeight: isOwner ? 20 : undefined,
                  }}
                >
                  {profile.bio || (isOwner ? "No bio yet" : "")}
                </Typography>
                {isOwner && (
                  <IconButton size="small" onClick={handleStartEditBio} sx={{ mt: -0.5, opacity: 0.5, "&:hover": { opacity: 1 } }}>
                    <EditIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                )}
              </Box>
            )}
          </Box>

          {/* ── Settings (owner only) ── */}
          {isOwner && (
            <>
              <Divider sx={{ opacity: 0.15, my: 2.5 }} />

              <Accordion
                disableGutters
                elevation={0}
                sx={{
                  bgcolor: "transparent",
                  "&:before": { display: "none" },
                  "& .MuiAccordionSummary-root": { px: 0, minHeight: 40 },
                  "& .MuiAccordionDetails-root": { px: 0, pt: 0 },
                }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <SettingsIcon sx={{ fontSize: 18, opacity: 0.6 }} />
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {t("settings.title")}
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  {/* Appearance */}
                  <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                    {t("settings.appearance")}
                  </Typography>
                  <ToggleButtonGroup
                    value={themePreference}
                    exclusive
                    size="small"
                    fullWidth
                    onChange={(_, next) => next && setThemePreference(next)}
                    sx={{ mb: 1.5 }}
                  >
                    <ToggleButton value="light">{t("settings.lightMode")}</ToggleButton>
                    <ToggleButton value="dark">{t("settings.darkMode")}</ToggleButton>
                    <ToggleButton value="system">{t("settings.systemMode")}</ToggleButton>
                  </ToggleButtonGroup>

                  <Divider sx={{ opacity: 0.15, mb: 1.5 }} />

                  {/* Language */}
                  <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                    {t("settings.languageLabel")}
                  </Typography>
                  <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
                    <Select value={language} onChange={handleLanguageChange}>
                      {SUPPORTED_LANGUAGES.map((opt: any) => (
                        <MenuItem key={opt.code} value={opt.code}>{opt.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <Divider sx={{ opacity: 0.15 }} />

                  <ToggleRow
                    label="Privacy Mode"
                    caption="Hide your email and last name"
                    checked={privacyMode}
                    onChange={handlePrivacyModeToggle}
                  />

                  <Divider sx={{ opacity: 0.15 }} />

                  <ToggleRow
                    label="Public Profile"
                    caption="Others can view your profile page"
                    checked={publicProfile}
                    onChange={async () => {
                      const newValue = !publicProfile;
                      setPublicProfile(newValue);
                      setProfileSaving(true);
                      try {
                        await updateCommands.updateProfileVisibility(newValue);
                      } catch {
                        setPublicProfile(!newValue);
                      } finally {
                        setProfileSaving(false);
                      }
                    }}
                    disabled={profileSaving}
                  />

                  <Divider sx={{ opacity: 0.15 }} />

                  <ToggleRow
                    label="Promotional Emails"
                    caption="New features and site news"
                    checked={promoEmails}
                    onChange={async () => {
                      const newValue = !promoEmails;
                      setPromoEmails(newValue);
                      setPromoSaving(true);
                      try {
                        await updateCommands.updateEmailPreference(newValue);
                      } catch {
                        setPromoEmails(!newValue);
                      } finally {
                        setPromoSaving(false);
                      }
                    }}
                    disabled={promoSaving}
                  />

                  {/* ── Verification ── */}
                  {!profile.is_verified && (
                    <>
                      <Divider sx={{ opacity: 0.15, my: 1.5 }} />
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 1 }}>
                        <VerifiedIcon sx={{ fontSize: 16, opacity: 0.5 }} />
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Verification
                        </Typography>
                      </Box>

                      {/* Eligibility metrics */}
                      {profile.verification_eligibility && (
                        <Box sx={{ mb: 1.5 }}>
                          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
                            <Typography variant="caption" color="text.secondary">
                              Tier Lists Created
                            </Typography>
                            <Typography variant="caption" sx={{
                              fontWeight: 600,
                              color: profile.verification_eligibility.tierListCount >= profile.verification_eligibility.tierListRequired ? "success.main" : "text.secondary",
                            }}>
                              {profile.verification_eligibility.tierListCount} / {profile.verification_eligibility.tierListRequired}
                            </Typography>
                          </Box>
                          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <Typography variant="caption" color="text.secondary">
                              Unique Visit Days
                            </Typography>
                            <Typography variant="caption" sx={{
                              fontWeight: 600,
                              color: profile.verification_eligibility.uniqueVisitDays >= profile.verification_eligibility.visitDaysRequired ? "success.main" : "text.secondary",
                            }}>
                              {profile.verification_eligibility.uniqueVisitDays} / {profile.verification_eligibility.visitDaysRequired}
                            </Typography>
                          </Box>
                        </Box>
                      )}

                      {profile.has_pending_verification ? (
                        <Alert severity="info" sx={{ py: 0.5 }} variant="outlined">
                          Your verification request is pending review.
                        </Alert>
                      ) : profile.verification_eligibility?.eligible ? (
                        <>
                          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                            Tell us why you should be verified (e.g. artist, public figure, notable contributor).
                          </Typography>
                          <TextField
                            fullWidth
                            size="small"
                            value={verifyReason}
                            onChange={(e) => setVerifyReason(e.target.value)}
                            disabled={verifySubmitting}
                            placeholder="Why should you be verified?"
                            inputProps={{ maxLength: 1000 }}
                            multiline
                            rows={3}
                            sx={{ mb: 1 }}
                          />
                          <Button
                            variant="contained"
                            size="small"
                            fullWidth
                            onClick={handleRequestVerification}
                            disabled={verifySubmitting || verifyReason.trim().length < 10}
                            sx={{ textTransform: "none" }}
                          >
                            {verifySubmitting ? <CircularProgress size={16} sx={{ color: "inherit" }} /> : "Submit Request"}
                          </Button>
                          {verifyStatus.message && (
                            <Alert
                              severity={verifyStatus.type === "success" ? "success" : "error"}
                              sx={{ mt: 1, py: 0 }}
                              variant="outlined"
                            >
                              {verifyStatus.message}
                            </Alert>
                          )}
                        </>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          Meet the requirements above to request verification.
                        </Typography>
                      )}
                    </>
                  )}

                  <Divider sx={{ opacity: 0.15, my: 2 }} />

                  <Button
                    variant="outlined"
                    color="error"
                    fullWidth
                    size="small"
                    onClick={() => props?.mainFunctions?.handleSignOut()}
                    sx={{ textTransform: "none", fontWeight: 600, py: 0.75 }}
                  >
                    Sign Out
                  </Button>

                  <Box sx={{ mt: 1.5 }}>
                    <Link href="/PrivacyPolicy" rel="noopener" underline="hover"
                      sx={{ fontSize: "0.75rem", color: "var(--text-secondary)" }}>
                      {t("navigation.privacyPolicy")}
                    </Link>
                  </Box>
                </AccordionDetails>
              </Accordion>
            </>
          )}
        </Box>

        {/* ── Right Content Area ── */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {/* Section header with view toggle */}
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Typography
                variant="h6"
                onClick={() => setActiveSection("tierlists")}
                sx={{
                  fontWeight: 600,
                  cursor: "pointer",
                  opacity: activeSection === "tierlists" ? 1 : 0.5,
                  borderBottom: activeSection === "tierlists" ? "2px solid white" : "2px solid transparent",
                  pb: 0.25,
                  transition: "opacity 0.15s ease",
                }}
              >
                {isOwner ? "Tier Lists" : "Published Tier Lists"}
                {isOwner && (
                  <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                    ({(profile.captures || []).filter((c: any) => !c.type?.startsWith("Grid")).length})
                  </Typography>
                )}
              </Typography>
              <Typography
                variant="h6"
                onClick={() => setActiveSection("grids")}
                sx={{
                  fontWeight: 600,
                  cursor: "pointer",
                  opacity: activeSection === "grids" ? 1 : 0.5,
                  borderBottom: activeSection === "grids" ? "2px solid white" : "2px solid transparent",
                  pb: 0.25,
                  transition: "opacity 0.15s ease",
                }}
              >
                Grids
                {isOwner && (
                  <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                    ({(profile.captures || []).filter((c: any) => c.type?.startsWith("Grid")).length})
                  </Typography>
                )}
              </Typography>
              {isOwner && (
                <Typography
                  variant="h6"
                  onClick={() => {
                    setActiveSection("liked");
                    fetchLikedItems();
                  }}
                  sx={{
                    fontWeight: 600,
                    cursor: "pointer",
                    opacity: activeSection === "liked" ? 1 : 0.5,
                    borderBottom: activeSection === "liked" ? "2px solid white" : "2px solid transparent",
                    pb: 0.25,
                    transition: "opacity 0.15s ease",
                    display: "flex",
                    alignItems: "center",
                    gap: 0.75,
                  }}
                >
                  <FavoriteIcon sx={{ fontSize: 20 }} />
                  Liked
                </Typography>
              )}
            </Box>
            {(activeSection === "tierlists" || activeSection === "grids") && (
              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={(_, v) => v && setViewMode(v)}
                size="small"
              >
                <ToggleButton value="grid" sx={{ px: 1, py: 0.5 }}>
                  <GridViewIcon fontSize="small" />
                </ToggleButton>
                <ToggleButton value="list" sx={{ px: 1, py: 0.5 }}>
                  <ViewListIcon fontSize="small" />
                </ToggleButton>
              </ToggleButtonGroup>
            )}
          </Box>

          {/* Liked Section (owner only, private) */}
          {isOwner && activeSection === "liked" && (
            likedLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                <CircularProgress size={32} />
              </Box>
            ) : likedItems.length > 0 ? (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "repeat(2, 1fr)",
                    md: "repeat(3, 1fr)",
                  },
                  gap: 2.5,
                }}
              >
                {likedItems.map((item: any) => {
                  const displayItemName = item.alias_name || item.user_name?.split(" ")[0] || "Anonymous";
                  return (
                    <Card
                      key={item.Id}
                      sx={{
                        transition: "transform 0.2s ease, box-shadow 0.2s ease",
                        "&:hover": {
                          transform: "translateY(-4px)",
                          boxShadow: "0 8px 24px rgba(0,0,0,0.4)",
                        },
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      {item.thumbnail ? (
                        <CardMedia
                          component="img"
                          image={item.thumbnail}
                          alt={item.title || "Tier List"}
                          sx={{ height: 200, objectFit: "cover" }}
                        />
                      ) : (
                        <Box
                          sx={{
                            height: 200,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            bgcolor: "var(--hover-overlay)",
                          }}
                        >
                          <Typography variant="body2" color="text.secondary">
                            No preview
                          </Typography>
                        </Box>
                      )}
                      <CardContent sx={{ pb: 1.5 }}>
                        <Typography variant="subtitle1" noWrap sx={{ fontWeight: 600 }}>
                          {item.title || item.type || "Untitled"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          by{" "}
                          <RouterLink
                            to={`/profile/${item.alias_name || item.user_id}`}
                            style={{ color: "inherit", textDecoration: "none" }}
                            onMouseEnter={(e) => (e.currentTarget.style.textDecoration = "underline")}
                            onMouseLeave={(e) => (e.currentTarget.style.textDecoration = "none")}
                          >
                            {displayItemName}
                          </RouterLink>
                        </Typography>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mt: 0.75 }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            <ThumbUpIcon sx={{ fontSize: 14, opacity: 0.6 }} />
                            <Typography variant="caption" color="text.secondary">
                              {item.like_count || 0}
                            </Typography>
                          </Box>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            <VisibilityIcon sx={{ fontSize: 14, opacity: 0.6 }} />
                            <Typography variant="caption" color="text.secondary">
                              {item.view_count || 0}
                            </Typography>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  );
                })}
              </Box>
            ) : (
              <Box sx={{ textAlign: "center", py: 8 }}>
                <Typography variant="h6" color="text.secondary">
                  No liked tier lists yet.
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Like tier lists in the community to see them here.
                </Typography>
              </Box>
            )
          )}

          {/* Tier Lists / Grids */}
          {(activeSection === "tierlists" || activeSection === "grids") && (filteredCaptures.length > 0 ? (
            viewMode === "grid" ? (
              /* ── Grid View ── */
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "repeat(2, 1fr)",
                    md: "repeat(3, 1fr)",
                  },
                  gap: 2.5,
                }}
              >
                {filteredCaptures.map((capture: any) => {
                  const isDraft = capture.is_public !== 1;
                  return (
                    <Card
                      key={capture.Id}
                      onClick={() => handleCardClick(capture)}
                      sx={{
                        cursor: "pointer",
                        transition: "transform 0.2s ease, box-shadow 0.2s ease",
                        "&:hover": {
                          transform: "translateY(-4px)",
                          boxShadow: "0 8px 24px rgba(0,0,0,0.4)",
                        },
                        display: "flex",
                        flexDirection: "column",
                        position: "relative",
                        opacity: isDraft ? 0.65 : 1,
                      }}
                    >
                      {isOwner && postCommands && (
                        <Box sx={{
                          position: "absolute",
                          top: 8,
                          right: 8,
                          zIndex: 1,
                          display: "flex",
                          flexDirection: "column",
                          gap: 0.5,
                        }}>
                          <Tooltip title={isDraft ? "Publish" : "Unpublish"}>
                            <IconButton
                              size="small"
                              onClick={(e) => handleTogglePublishCard(e, capture)}
                              disabled={unpublishingCard === capture.Id}
                              sx={{
                                bgcolor: "var(--on-media-bg)",
                                "&:hover": { bgcolor: "var(--on-media-bg-hover)" },
                                color: "var(--on-media)",
                              }}
                            >
                              {unpublishingCard === capture.Id ? (
                                <CircularProgress size={18} sx={{ color: "var(--on-media)" }} />
                              ) : isDraft ? (
                                <PublishIcon fontSize="small" />
                              ) : (
                                <VisibilityOffIcon fontSize="small" />
                              )}
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Share">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                const url = `${BASE_SITE_URL}/share/${capture.Id}`;
                                navigator.clipboard.writeText(url).then(() => {
                                  toast.success(t("toast.linkCopied") || "Link copied!");
                                }).catch(() => {});
                              }}
                              sx={{
                                bgcolor: "var(--on-media-bg)",
                                "&:hover": { bgcolor: "var(--on-media-bg-hover)" },
                                color: "var(--on-media)",
                              }}
                            >
                              <ShareIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {confirmDeleteId === capture.Id ? (
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                              <IconButton
                                size="small"
                                onClick={(e) => { e.stopPropagation(); handleDeleteCapture(capture.Id); }}
                                disabled={deletingId === capture.Id}
                                sx={{
                                  bgcolor: "rgba(211,47,47,0.85)",
                                  "&:hover": { bgcolor: "rgba(211,47,47,1)" },
                                  color: "#ffffff",
                                }}
                              >
                                {deletingId === capture.Id ? (
                                  <CircularProgress size={18} sx={{ color: "var(--on-media)" }} />
                                ) : (
                                  <CheckIcon fontSize="small" />
                                )}
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={(e) => { e.stopPropagation(); setConfirmDeleteId(null); }}
                                sx={{
                                  bgcolor: "var(--on-media-bg)",
                                  "&:hover": { bgcolor: "var(--on-media-bg-hover)" },
                                  color: "var(--on-media)",
                                }}
                              >
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          ) : (
                            <Tooltip title="Delete">
                              <IconButton
                                size="small"
                                onClick={(e) => { e.stopPropagation(); setConfirmDeleteId(capture.Id); }}
                                sx={{
                                  bgcolor: "var(--on-media-bg)",
                                  "&:hover": { bgcolor: "rgba(211,47,47,0.85)" },
                                  color: "var(--on-media)",
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      )}
                      {isDraft && isOwner && (
                        <Chip
                          label="Draft"
                          size="small"
                          sx={{
                            position: "absolute",
                            top: 8,
                            left: 8,
                            zIndex: 1,
                            fontSize: "0.65rem",
                            height: 22,
                            bgcolor: "var(--on-media-bg)",
                            color: "var(--on-media)",
                          }}
                        />
                      )}
                      {capture.thumbnail ? (
                        <CardMedia
                          component="img"
                          image={capture.thumbnail}
                          alt={capture.title || "Tier List"}
                          sx={{ height: 200, objectFit: "cover" }}
                        />
                      ) : (
                        <Box
                          sx={{
                            height: 200,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            bgcolor: "var(--hover-overlay)",
                          }}
                        >
                          <Typography variant="body2" color="text.secondary">
                            No preview
                          </Typography>
                        </Box>
                      )}
                      <CardContent sx={{ pb: 1.5 }}>
                        <Typography variant="subtitle1" noWrap sx={{ fontWeight: 600 }}>
                          {capture.title || capture.type || "Untitled"}
                        </Typography>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
                          {capture.list_type && (
                            <Chip
                              label={getTypeLabel(capture)}
                              size="small"
                              sx={{ fontSize: "0.65rem", height: 20 }}
                            />
                          )}
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mt: 0.75 }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            <ThumbUpIcon sx={{ fontSize: 14, opacity: 0.6 }} />
                            <Typography variant="caption" color="text.secondary">
                              {capture.like_count || 0}
                            </Typography>
                          </Box>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            <VisibilityIcon sx={{ fontSize: 14, opacity: 0.6 }} />
                            <Typography variant="caption" color="text.secondary">
                              {capture.view_count || 0}
                            </Typography>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  );
                })}
              </Box>
            ) : (
              /* ── List View ── */
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {filteredCaptures.map((capture: any) => {
                  const isDraft = capture.is_public !== 1;
                  return (
                    <Card
                      key={capture.Id}
                      onClick={() => handleCardClick(capture)}
                      sx={{
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        p: 1.5,
                        opacity: isDraft ? 0.65 : 1,
                        transition: "background 0.15s ease",
                        "&:hover": { bgcolor: "var(--hover-overlay)" },
                      }}
                    >
                      {/* Thumbnail */}
                      {capture.thumbnail ? (
                        <Box
                          component="img"
                          src={capture.thumbnail}
                          alt={capture.title || "Tier List"}
                          sx={{
                            width: 80,
                            height: 56,
                            borderRadius: 1,
                            objectFit: "cover",
                            flexShrink: 0,
                          }}
                        />
                      ) : (
                        <Box sx={{
                          width: 80,
                          height: 56,
                          borderRadius: 1,
                          bgcolor: "var(--hover-overlay)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}>
                          <Typography variant="caption" color="text.secondary">—</Typography>
                        </Box>
                      )}

                      {/* Info */}
                      <Box sx={{ flex: 1, minWidth: 0, mx: 2 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
                            {capture.title || capture.type || "Untitled"}
                          </Typography>
                          {isDraft && isOwner && (
                            <Chip label="Draft" size="small" sx={{ fontSize: "0.6rem", height: 18 }} />
                          )}
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mt: 0.25 }}>
                          {getTypeLabel(capture) && (
                            <Typography variant="caption" color="text.secondary">
                              {getTypeLabel(capture)}
                            </Typography>
                          )}
                          {capture.created_date && (
                            <Typography variant="caption" color="text.secondary" sx={{ opacity: 0.6 }}>
                              {new Date(capture.created_date).toLocaleDateString(undefined, {
                                year: "numeric",
                                month: "short",
                                day: "numeric",
                              })}
                            </Typography>
                          )}
                        </Box>
                      </Box>

                      {/* Stats + Actions */}
                      <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexShrink: 0 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <ThumbUpIcon sx={{ fontSize: 14, opacity: 0.5 }} />
                          <Typography variant="caption" color="text.secondary">
                            {capture.like_count || 0}
                          </Typography>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <VisibilityIcon sx={{ fontSize: 14, opacity: 0.5 }} />
                          <Typography variant="caption" color="text.secondary">
                            {capture.view_count || 0}
                          </Typography>
                        </Box>
                        {isOwner && postCommands && (
                          <>
                            <Tooltip title={isDraft ? "Publish" : "Unpublish"}>
                              <IconButton
                                size="small"
                                onClick={(e) => handleTogglePublishCard(e, capture)}
                                disabled={unpublishingCard === capture.Id}
                                sx={{ ml: 0.5 }}
                              >
                                {unpublishingCard === capture.Id ? (
                                  <CircularProgress size={16} />
                                ) : isDraft ? (
                                  <PublishIcon fontSize="small" />
                                ) : (
                                  <VisibilityOffIcon fontSize="small" />
                                )}
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Share">
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const url = `${BASE_SITE_URL}/share/${capture.Id}`;
                                  navigator.clipboard.writeText(url).then(() => {
                                    toast.success(t("toast.linkCopied") || "Link copied!");
                                  }).catch(() => {});
                                }}
                              >
                                <ShareIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            {confirmDeleteId === capture.Id ? (
                              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                <IconButton
                                  size="small"
                                  onClick={(e) => { e.stopPropagation(); handleDeleteCapture(capture.Id); }}
                                  disabled={deletingId === capture.Id}
                                  sx={{ color: "error.main" }}
                                >
                                  {deletingId === capture.Id ? (
                                    <CircularProgress size={16} />
                                  ) : (
                                    <CheckIcon fontSize="small" />
                                  )}
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={(e) => { e.stopPropagation(); setConfirmDeleteId(null); }}
                                >
                                  <CloseIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            ) : (
                              <Tooltip title="Delete">
                                <IconButton
                                  size="small"
                                  onClick={(e) => { e.stopPropagation(); setConfirmDeleteId(capture.Id); }}
                                  sx={{ color: "text.secondary", "&:hover": { color: "error.main" } }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </>
                        )}
                      </Box>
                    </Card>
                  );
                })}
              </Box>
            )
          ) : (
            <Box sx={{ textAlign: "center", py: 8 }}>
              <Typography variant="h6" color="text.secondary">
                {activeSection === "grids"
                  ? (isOwner ? "No grids yet." : "No published grids yet.")
                  : (isOwner ? "No tier lists yet." : "No published tier lists yet.")}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* View Capture Dialog */}
      {viewingCapture && (
        <Dialog
          open
          onClose={() => setViewingCapture(null)}
          maxWidth="lg"
          fullWidth
          PaperProps={{ sx: { borderRadius: 2 } }}
        >
          {/* Header */}
          <Box sx={{ px: 3, pt: 2.5, pb: 1.5 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                {isOwner && editingTitle ? (
                  <TextField
                    value={editTitle}
                    onChange={(e) => setEditTitle(e.target.value)}
                    onBlur={handleSaveTitle}
                    onKeyDown={(e) => { if (e.key === "Enter") handleSaveTitle(); if (e.key === "Escape") setEditingTitle(false); }}
                    autoFocus
                    variant="standard"
                    inputProps={{ maxLength: 100 }}
                    slotProps={{ input: { style: { fontSize: "1.25rem", fontWeight: 700 } } }}
                    sx={{ width: "100%" }}
                  />
                ) : (
                  <Box
                    onClick={() => {
                      if (!isOwner) return;
                      setEditTitle(viewingCapture.title || viewingCapture.type || "");
                      setEditingTitle(true);
                    }}
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 1,
                      cursor: isOwner ? "pointer" : "default",
                      "&:hover .edit-icon": { opacity: isOwner ? 1 : 0 },
                    }}
                  >
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      {viewingCapture.title || viewingCapture.type || "Tier List"}
                    </Typography>
                    {isOwner && (
                      <EditIcon className="edit-icon" sx={{ fontSize: 16, opacity: 0, transition: "opacity 0.15s" }} />
                    )}
                  </Box>
                )}
                {/* Description */}
                {isOwner && editingDescription ? (
                  <TextField
                    value={editDescription}
                    onChange={(e) => setEditDescription(e.target.value)}
                    onBlur={handleSaveDescription}
                    onKeyDown={(e) => { if (e.key === "Escape") setEditingDescription(false); }}
                    autoFocus
                    variant="standard"
                    multiline
                    maxRows={3}
                    placeholder="Add a description..."
                    inputProps={{ maxLength: 500 }}
                    sx={{ width: "100%", mt: 0.5 }}
                  />
                ) : (
                  <Box
                    onClick={() => {
                      if (!isOwner) return;
                      setEditDescription(viewingCapture.description || "");
                      setEditingDescription(true);
                    }}
                    sx={{ cursor: isOwner ? "pointer" : "default", mt: 0.5 }}
                  >
                    <Typography
                      variant="body2"
                      color={viewingCapture.description ? "text.primary" : "text.disabled"}
                      sx={{
                        "&:hover": isOwner ? { textDecoration: "underline" } : {},
                      }}
                    >
                      {viewingCapture.description || (isOwner ? "Add a description..." : "")}
                    </Typography>
                  </Box>
                )}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5, flexWrap: "wrap" }}>
                  {getTypeLabel(viewingCapture) && (
                    <Chip
                      label={getTypeLabel(viewingCapture)}
                      size="small"
                      sx={{ fontSize: "0.7rem", height: 22 }}
                    />
                  )}
                  {viewingCapture.published_date && (
                    <Typography variant="caption" color="text.secondary">
                      {new Date(viewingCapture.published_date).toLocaleDateString(undefined, {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                      })}
                    </Typography>
                  )}
                </Box>
              </Box>
              {/* Stats */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, ml: 2, flexShrink: 0 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <ThumbUpIcon sx={{ fontSize: 18, opacity: 0.6 }} />
                  <Typography variant="body2">{viewingCapture.like_count || 0}</Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <ThumbDownIcon sx={{ fontSize: 18, opacity: 0.6 }} />
                  <Typography variant="body2">{viewingCapture.dislike_count || 0}</Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <VisibilityIcon sx={{ fontSize: 18, opacity: 0.6 }} />
                  <Typography variant="body2">{viewingCapture.view_count || 0}</Typography>
                </Box>
                {viewingCapture.try_count > 0 && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <PeopleIcon sx={{ fontSize: 18, opacity: 0.6 }} />
                    <Typography variant="body2">{viewingCapture.try_count}</Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </Box>

          <Divider sx={{ opacity: 0.15 }} />

          {/* Image */}
          <DialogContent sx={{ textAlign: "center", p: 2 }}>
            {viewLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                <CircularProgress />
              </Box>
            ) : viewingCapture.screenshotInfo ? (
              <Box
                component="img"
                src={viewingCapture.screenshotInfo}
                alt={viewingCapture.title || "Tier List"}
                sx={{
                  maxWidth: "100%",
                  maxHeight: "70vh",
                  objectFit: "contain",
                  borderRadius: 1,
                }}
              />
            ) : viewingCapture.thumbnail ? (
              <Box
                component="img"
                src={viewingCapture.thumbnail}
                alt={viewingCapture.title || "Tier List"}
                sx={{
                  maxWidth: "100%",
                  maxHeight: "70vh",
                  objectFit: "contain",
                  borderRadius: 1,
                }}
              />
            ) : (
              <Typography color="text.secondary">Image not available</Typography>
            )}
          </DialogContent>

          {/* Footer */}
          <DialogActions sx={{ px: 3, py: 1.5 }}>
            {isOwner && postCommands && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, mr: "auto" }}>
                <Button
                  onClick={handleTogglePublish}
                  variant="outlined"
                  size="small"
                  color={viewingCapture?.is_public === 1 ? "error" : "success"}
                  disabled={togglingPublish}
                  startIcon={
                    togglingPublish ? <CircularProgress size={14} /> : viewingCapture?.is_public === 1 ? <VisibilityOffIcon /> : <PublishIcon />
                  }
                  sx={{ textTransform: "none" }}
                >
                  {viewingCapture?.is_public === 1 ? t("community.unpublish") : "Publish"}
                </Button>
                {viewingCapture?.is_public === 1 && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      {t("capture.allowTry")}
                    </Typography>
                    <Switch
                      checked={viewingCapture?.allow_try === 1}
                      onChange={handleToggleTry}
                      disabled={togglingTry}
                      size="small"
                      sx={{
                        "& .MuiSwitch-switchBase.Mui-checked": { color: "var(--accent)" },
                        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "var(--accent)" },
                      }}
                    />
                  </Box>
                )}
                <Button
                  onClick={() => {
                    const url = `${BASE_SITE_URL}/share/${viewingCapture?.Id}`;
                    navigator.clipboard.writeText(url).then(() => {
                      toast.success(t("toast.linkCopied") || "Link copied!");
                    }).catch(() => {});
                  }}
                  variant="outlined"
                  size="small"
                  startIcon={<ShareIcon />}
                  sx={{ textTransform: "none" }}
                >
                  Share
                </Button>
                {confirmDeleteId === viewingCapture?.Id ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography variant="caption" color="error">Delete?</Typography>
                    <Button
                      size="small"
                      color="error"
                      variant="contained"
                      onClick={() => handleDeleteCapture(viewingCapture.Id)}
                      disabled={deletingId === viewingCapture.Id}
                      sx={{ textTransform: "none", minWidth: 0, px: 1.5 }}
                    >
                      {deletingId === viewingCapture.Id ? <CircularProgress size={14} sx={{ color: "inherit" }} /> : "Yes"}
                    </Button>
                    <Button
                      size="small"
                      onClick={() => setConfirmDeleteId(null)}
                      sx={{ textTransform: "none", minWidth: 0, px: 1.5 }}
                    >
                      No
                    </Button>
                  </Box>
                ) : (
                  <Button
                    onClick={() => setConfirmDeleteId(viewingCapture?.Id)}
                    variant="outlined"
                    size="small"
                    color="error"
                    startIcon={<DeleteIcon />}
                    sx={{ textTransform: "none" }}
                  >
                    Delete
                  </Button>
                )}
              </Box>
            )}
            <Button onClick={() => setViewingCapture(null)} sx={{ textTransform: "none" }}>
              Close
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};

export default Profile;
